import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Check,
  Clock3,
  Coins,
  Crown,
  Gift,
  Star,
  Trophy,
  Wallet,
  X,
} from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import apiService from '../../utils/apiService';
import { useFetchOnceByKey } from '../../hooks/useMountOnce';
import {
  formatPts,
  formatPtsLabel,
  toIntPercent,
} from '../../utils/moduleHelpers';
import { buildPointsBreakdownFromHistory } from '../../utils/loyaltyData';
import '../../styles/module-shared.css';
import './loyalty-module.css';

const DONUT_COLORS = ['#a52a2a', '#6b0000', '#22c55e', '#eab308', '#ec4899', '#8b0000'];

const TIER_META = {
  bronze: { theme: 'bronze', color: '#b45309' },
  silver: { theme: 'silver', color: '#64748b' },
  gold: { theme: 'gold', color: '#eab308' },
  platinum: { theme: 'platinum', color: '#8b0000' },
  diamond: { theme: 'diamond', color: '#22d3ee' },
};

const usdFromPoints = (pts) => {
  const dollars = Number(pts || 0) / 100;
  return `$${dollars.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const resolveTierMeta = (name, index) => {
  const key = String(name || '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  return (
    TIER_META[key] || {
      theme: ['bronze', 'silver', 'gold', 'platinum', 'diamond'][index % 5],
      color: ['#b45309', '#64748b', '#eab308', '#8b0000', '#a52a2a'][index % 5],
    }
  );
};

const Donut = ({ slices, centerValue }) => {
  const total = slices.reduce((sum, s) => sum + s.points, 0) || 1;
  let offset = 0;
  const r = 36;
  const c = 2 * Math.PI * r;
  const [tooltip, setTooltip] = useState(null);

  return (
    <div
      className="lp-donut"
      onMouseLeave={() => setTooltip(null)}
    >
      <svg viewBox="0 0 100 100" aria-label="Points breakdown chart">
        <circle cx="50" cy="50" r={r} fill="none" stroke="#eef2ff" strokeWidth="12" />
        {slices.map((slice, i) => {
          const pct =
            slice.percent != null
              ? slice.percent
              : Math.round((slice.points / total) * 100);
          const len = (slice.points / total) * c;
          const color = DONUT_COLORS[i % DONUT_COLORS.length];
          const el = (
            <circle
              key={`${slice.name}-${i}`}
              className="lp-donut__seg"
              cx="50"
              cy="50"
              r={r}
              fill="none"
              stroke={color}
              strokeWidth="12"
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
              onMouseEnter={(e) => {
                const rect = e.currentTarget.ownerSVGElement?.parentElement?.getBoundingClientRect();
                const x = e.clientX - (rect?.left || 0);
                const y = e.clientY - (rect?.top || 0);
                setTooltip({
                  name: slice.name,
                  points: slice.points,
                  percent: pct,
                  color,
                  x: Math.min(Math.max(x, 8), (rect?.width || 160) - 8),
                  y: Math.max(y - 8, 8),
                });
              }}
              onMouseMove={(e) => {
                const rect = e.currentTarget.ownerSVGElement?.parentElement?.getBoundingClientRect();
                const x = e.clientX - (rect?.left || 0);
                const y = e.clientY - (rect?.top || 0);
                setTooltip((prev) =>
                  prev
                    ? {
                        ...prev,
                        x: Math.min(Math.max(x, 8), (rect?.width || 160) - 8),
                        y: Math.max(y - 8, 8),
                      }
                    : prev
                );
              }}
            >
              <title>
                {slice.name}: {formatPts(slice.points)} PTS ({pct}%)
              </title>
            </circle>
          );
          offset += len;
          return el;
        })}
      </svg>
      <div className="lp-donut__center">
        <strong className="pts">{formatPts(centerValue)} PTS</strong>
        <span>TOTAL</span>
      </div>
      {tooltip ? (
        <div
          className="lp-donut__tooltip"
          style={{ left: tooltip.x, top: tooltip.y }}
          role="tooltip"
        >
          <span className="lp-donut__tooltip-dot" style={{ background: tooltip.color }} />
          <div>
            <strong>{tooltip.name}</strong>
            <em>
              {tooltip.percent}% · {formatPts(tooltip.points)} PTS
            </em>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const LoyaltyDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [tiersList, setTiersList] = useState([]);
  const [tiersOpen, setTiersOpen] = useState(false);

  const fetchData = useCallback(async ({ cancelled }) => {
    setLoading(true);
    setError('');
    try {
      const [summaryRes, tiersRes, historyRes] = await Promise.all([
        apiService.getLoyaltySummary(),
        apiService.getLoyaltyTiersList().catch(() => ({ success: false, data: [] })),
        apiService
          .getLoyaltyPointsHistory({ pageNumber: 1, pageSize: 500 })
          .catch(() => ({ success: false, data: { history: [] } })),
      ]);
      if (cancelled()) return;

      if (!summaryRes.success) {
        setError(summaryRes.error || 'Failed to load loyalty dashboard');
        setSummary(null);
        setTiersList([]);
        setHistoryRows([]);
        return;
      }

      setSummary(summaryRes.data || {});
      // Modal + tier cards: GET /Loyalty/GetLoyaltyTiersList only
      setTiersList(Array.isArray(tiersRes?.data) ? tiersRes.data : []);
      setHistoryRows(Array.isArray(historyRes?.data?.history) ? historyRes.data.history : []);
    } catch (err) {
      if (!cancelled()) setError(err.message || 'Failed to load loyalty dashboard');
    } finally {
      if (!cancelled()) setLoading(false);
    }
  }, []);

  useFetchOnceByKey('loyalty-dashboard-history-v2', fetchData, true);

  const loyaltyPoints = Number(summary?.availableLoyaltyPoints ?? summary?.loyaltyPoints ?? 0);
  const lifetime = Number(summary?.totalLoyaltyPoints ?? summary?.totalPointsEarned ?? loyaltyPoints);
  const available = Number(summary?.availableLoyaltyPoints ?? summary?.availablePoints ?? loyaltyPoints);
  const redeemed = Number(summary?.pointsRedeemed ?? Math.max(0, lifetime - available));
  const monthEarned = Number(summary?.pointsEarnedThisMonth ?? 0);
  const expiring = Number(summary?.expiringSoon ?? 0);

  const breakdown = useMemo(() => {
    // Prefer full history; fall back to summary recentHistory (still earned rows, not catalog rates)
    const rows =
      (Array.isArray(historyRows) && historyRows.length
        ? historyRows
        : null) ||
      summary?.recentHistory ||
      [];

    const fromHistory = buildPointsBreakdownFromHistory(rows);
    if (fromHistory.slices.length) {
      return fromHistory.slices;
    }

    return [];
  }, [historyRows, summary]);

  const breakdownTotal = useMemo(
    () => breakdown.reduce((sum, item) => sum + item.points, 0),
    [breakdown]
  );

  const tiers = useMemo(() => {
    // Source of truth for modal / progress: GetLoyaltyTiersList
    const list = tiersList.length ? tiersList : summary?.tiers || [];
    const mapped = list.map((t, i) => {
      const name = t.name || t.tierName || `Tier ${i + 1}`;
      const meta = resolveTierMeta(name, i);
      const required = Number(t.required ?? t.requiredPoints ?? t.tierPoints ?? 0);
      const allBenefits = Array.isArray(t.benefits) ? t.benefits.filter(Boolean) : [];
      const tagline = allBenefits.length > 1 ? allBenefits[0] : '';
      const benefits = allBenefits.length > 1 ? allBenefits.slice(1) : allBenefits;
      return {
        id: t.id || t.affiliateLoyaltyTierId || i,
        name,
        required,
        tagline,
        benefits,
        theme: meta.theme,
        color: meta.color,
      };
    });

    mapped.sort((a, b) => a.required - b.required);

    let currentIndex = 0;
    mapped.forEach((tier, i) => {
      if (lifetime >= tier.required) currentIndex = i;
    });

    return mapped.map((tier, i) => ({
      ...tier,
      isCurrent: i === currentIndex,
      unlocked: lifetime >= tier.required,
    }));
  }, [tiersList, summary, lifetime]);

  const currentTier = tiers.find((t) => t.isCurrent) || tiers[0] || null;
  const nextTier = currentTier
    ? tiers.find((t) => t.required > currentTier.required) || null
    : tiers[1] || null;

  const tierName = currentTier?.name || summary?.tierName || '—';
  const tierTagline =
    currentTier?.tagline ||
    currentTier?.benefits?.[0] ||
    'Keep earning to unlock more benefits.';
  const nextTierName = nextTier?.name || '';
  const nextTierPoints = Number(nextTier?.required ?? 0);
  const progressPct = toIntPercent(lifetime, nextTierPoints || lifetime || 1);
  const pointsToNext = Math.max(0, nextTierPoints - lifetime);

  const stats = [
    {
      label: 'Total Points Earned',
      value: formatPtsLabel(lifetime),
      sub: 'Lifetime',
      Icon: Trophy,
      tone: 'violet',
    },
    {
      label: 'Available to Use',
      value: formatPtsLabel(available),
      sub: 'Redeem and enjoy',
      Icon: Wallet,
      tone: 'green',
    },
    {
      label: 'Expiring Soon',
      value: formatPtsLabel(expiring),
      sub: expiring > 0 ? 'Use before they expire' : 'No expiry soon',
      Icon: Clock3,
      tone: 'amber',
    },
    {
      label: 'Points Redeemed',
      value: formatPtsLabel(redeemed),
      sub: 'All time',
      Icon: Gift,
      tone: 'blue',
    },
  ];

  return (
    <div className="page lp-page">
      <div className="page-inner">
        <ModulePageHeader
          icon={Trophy}
          title="Loyalty Points"
          subtitle="Earn loyalty points with every activity and unlock exciting rewards."
          iconBg="linear-gradient(135deg,#6b0000,#8b0000)"
        />
        <ModuleAlert message={error} />

        {loading ? (
          <div className="module-card">
            <AppLoader color="#8b0000" />
          </div>
        ) : (
          <>
            <section className="lp-hero">
              <div className="lp-hero__block">
                <div className="lp-hero__label">Your Loyalty Points</div>
                <div className="lp-hero__points-row">
                  <div>
                    <div className="lp-hero__value pts">
                      {formatPts(loyaltyPoints)}
                      <span className="pts-unit">PTS</span>
                    </div>
                    <div className="lp-hero__meta">≈ {usdFromPoints(loyaltyPoints)}</div>
                  </div>
                  <div className="lp-hero__coin" aria-hidden="true">
                    <Coins className="w-7 h-7" />
                  </div>
                </div>
              </div>

              <div className="lp-hero__block">
                <div className="lp-hero__label">Your Tier</div>
                <span className="lp-tier-badge">
                  <Crown className="w-3.5 h-3.5" />
                  {tierName}
                </span>
                <p className="lp-hero__tagline">{tierTagline}</p>
                <button type="button" className="lp-btn-amber" onClick={() => setTiersOpen(true)}>
                  View Tier Benefits
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="lp-hero__block">
                <div className="lp-hero__label">Next Tier</div>
                {nextTierName ? (
                  <>
                    <div className="lp-hero__value lp-hero__value--sm pts">
                      {formatPts(nextTierPoints)}
                      <span className="pts-unit">PTS</span>
                    </div>
                    <div className="lp-progress-wrap">
                      <div className="lp-progress">
                        <span style={{ width: `${progressPct}%` }} />
                      </div>
                      <span className="lp-next-pill">{nextTierName}</span>
                    </div>
                    <div className="lp-hero__meta">
                      {formatPts(pointsToNext)} pts more to reach {nextTierName}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="lp-hero__value lp-hero__value--sm">Max tier</div>
                    <div className="lp-hero__meta">You are at the highest loyalty tier</div>
                  </>
                )}
              </div>

              <div className="lp-hero__block">
                <div className="lp-hero__label">Points Earned (This Month)</div>
                <div className="lp-hero__points-row">
                  <div className="lp-hero__value pts">
                    {formatPts(monthEarned)}
                    <span className="pts-unit">PTS</span>
                  </div>
                  <div className="lp-hero__chart" aria-hidden="true">
                    <BarChart3 className="w-6 h-6" />
                  </div>
                </div>
              </div>
            </section>

            <section className="lp-stats">
              {stats.map((stat) => {
                const Icon = stat.Icon;
                return (
                  <article key={stat.label} className={`lp-stat lp-stat--${stat.tone}`}>
                    <div className="lp-stat__icon">
                      <Icon className="w-4 h-4" strokeWidth={2} />
                    </div>
                    <div className="lp-stat__value pts">{stat.value}</div>
                    <div className="lp-stat__label">{stat.label}</div>
                    <div className="lp-stat__sub">{stat.sub}</div>
                  </article>
                );
              })}
            </section>

            <section className="lp-mid">
              <div className="module-card lp-mid-card lp-breakdown">
                <h3>Points Breakdown</h3>
                {breakdown.length === 0 ? (
                  <p className="lp-empty">No breakdown data yet.</p>
                ) : (
                  <div className="lp-donut-wrap">
                    <Donut slices={breakdown} centerValue={breakdownTotal} />
                    <div className="lp-legend">
                      {breakdown.map((slice, i) => {
                        const pct =
                          slice.percent != null
                            ? slice.percent
                            : breakdownTotal > 0
                              ? Math.round((slice.points / breakdownTotal) * 100)
                              : 0;
                        return (
                          <div key={`${slice.name}-${i}`} className="lp-legend__row">
                            <div className="lp-legend__head">
                              <span className="lp-legend__name">
                                <span
                                  className="lp-legend__dot"
                                  style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }}
                                />
                                {slice.name}
                              </span>
                              <span className="lp-legend__meta">
                                <span className="lp-legend__pct">{pct}%</span>
                                <span className="lp-legend__pts pts">
                                  {formatPts(slice.points)} PTS
                                </span>
                              </span>
                            </div>
                            <div className="lp-legend__bar">
                              <span
                                style={{
                                  width: `${pct}%`,
                                  background: DONUT_COLORS[i % DONUT_COLORS.length],
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <Link className="lp-btn-gradient" to="/loyalty/history">
                  View detailed breakdown
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="module-card lp-mid-card lp-redeem-card">
                <div className="lp-redeem-card__head">
                  <h3>Redeem Your Points</h3>
                  <span className="lp-redeem-card__chip">
                    <Star className="w-3 h-3" />
                    Rewards
                  </span>
                </div>
                <p className="lp-redeem-card__copy">
                  Turn your loyalty points into amazing rewards.
                </p>

                <div className="lp-redeem-box">
                  <span>Available to redeem</span>
                  <strong className="pts">
                    {formatPts(available)} PTS
                    <em>≈ {usdFromPoints(available)}</em>
                  </strong>
                </div>

                <div className="lp-redeem__actions">
                  <Link className="lp-btn-primary" to="/rewards/claim">
                    Browse Rewards
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link className="lp-btn-ghost" to="/loyalty/history">
                    View Loyalty History
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </section>
          </>
        )}

        {tiersOpen ? (
          <div className="lp-modal-backdrop" onClick={() => setTiersOpen(false)} role="presentation">
            <div
              className="lp-modal lp-tiers-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="loyalty-tiers-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="lp-tiers-modal__head">
                <div>
                  <h3 id="loyalty-tiers-title">Loyalty Tiers &amp; Benefits</h3>
                  <p>
                    Based on {formatPts(lifetime)} lifetime PTS — you are{' '}
                    <strong>{tierName}</strong>
                  </p>
                </div>
                <button
                  type="button"
                  className="lp-modal__close"
                  onClick={() => setTiersOpen(false)}
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {tiers.length === 0 ? (
                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  Tier details are not available yet.
                </p>
              ) : (
                <div className="lp-tiers-grid">
                  {tiers.map((tier) => (
                    <article
                      key={tier.id}
                      className={`lp-tier-card theme-${tier.theme}${tier.isCurrent ? ' is-current' : ''}`}
                    >
                      <div className="lp-tier-card__top">
                        <div className="lp-tier-card__title">
                          <span className="lp-tier-card__dot" style={{ background: tier.color }} />
                          <span className="lp-tier-card__name">{tier.name}</span>
                        </div>
                        {tier.isCurrent ? <span className="lp-tier-pill">Current</span> : null}
                      </div>
                      <div className="lp-tier-card__req">From {formatPts(tier.required)} PTS</div>
                      {tier.tagline ? <p className="lp-tier-card__tagline">{tier.tagline}</p> : null}
                      {tier.benefits?.length ? (
                        <ul className="lp-tier-card__benefits">
                          {tier.benefits.map((b) => (
                            <li key={String(b)}>
                              <Check className="lp-tier-card__check" strokeWidth={2.5} />
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </article>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default LoyaltyDashboard;
